// "Show first" bottom sheet (DEV-50739 SP-002).
//
// Opened from the "+N more" button when the basket spans three or more
// branches. Lists EVERY basket branch (not only the hidden ones) with its
// supplier, branch and fulfilment method(s). Ticks are a local draft:
// Apply commits it, Clear empties the draft, and closing the sheet in any
// other way (X, backdrop, Escape) discards it. Same Radix bottom-sheet
// pattern as the cart's ChangeItemSheet.
import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Check, X } from 'lucide-react'
import { Button } from '../radix'
import type { BasketBranch } from '../../hooks/useBranchPriority'
import type { AtBranchSource } from '../../services/engineerSession'
import type { CartFulfillmentMethod } from '../../utils/cartFulfillment'

const METHOD_LABELS: Record<CartFulfillmentMethod, string> = {
  pickup: 'Collection',
  delivery: 'Delivery',
}

// Shown in place of the fulfilment methods for the location's counter, which
// has no basket line to read them from. Only a device fix justifies saying
// the engineer is there.
const HERE_LABELS: Record<AtBranchSource, string> = {
  device: "You're here",
  postcode: 'Near your postcode',
}

export interface BranchPrioritySheetProps {
  open: boolean
  onClose: () => void
  branches: BasketBranch[]
  // Keys ticked when the sheet opened — seeds the draft.
  initialSelectedKeys: string[]
  onApply: (keys: string[]) => void
}

export function BranchPrioritySheet({
  open,
  onClose,
  branches,
  initialSelectedKeys,
  onApply,
}: BranchPrioritySheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-white rounded-t-2xl shadow-xl focus:outline-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {/* Draft lives in a child that mounts with the sheet, so each open
              re-seeds from the current ticks without an effect. */}
          {open ? (
            <SheetBody
              branches={branches}
              initialSelectedKeys={initialSelectedKeys}
              onApply={onApply}
              onClose={onClose}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function SheetBody({
  branches,
  initialSelectedKeys,
  onApply,
  onClose,
}: Omit<BranchPrioritySheetProps, 'open'>) {
  const [draft, setDraft] = useState<string[]>(initialSelectedKeys)

  const toggleDraft = (key: string) => {
    setDraft((previous) =>
      previous.includes(key)
        ? previous.filter((selected) => selected !== key)
        : [...previous, key],
    )
  }

  return (
    <>
      {/* Drag handle */}
      <div className="flex justify-center pt-3 pb-1">
        <div className="h-1 w-9 rounded-full bg-gray-300" />
      </div>

      {/* Header */}
      <div className="px-4 py-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Dialog.Title className="text-sm font-bold text-gray-900">
            Which suppliers and branches should we show first?
          </Dialog.Title>
          <Dialog.Description className="text-[11px] leading-4 text-gray-500 mt-0.5">
            Choose from your basket. Matching products appear first, and your
            choices stay on for your next searches.
          </Dialog.Description>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex-none w-11 h-11 grid place-items-center rounded-full bg-gray-100 text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Branch list */}
      <div className="px-4 pb-3 space-y-1.5">
        {branches.map((branch) => {
          const checked = draft.includes(branch.key)
          return (
            <label
              key={branch.key}
              className={`flex min-h-12 cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-1.5 transition-colors focus-within:ring-2 focus-within:ring-brand-primary ${
                checked
                  ? 'border-brand-primary bg-brand-primary-tint'
                  : 'border-gray-300 bg-white'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleDraft(branch.key)}
                className="sr-only"
              />
              {/* Plain tick when checked (no box); the row border + tint
                  carry the state. A fixed-width slot keeps text aligned. */}
              <span aria-hidden="true" className="grid h-4 w-4 shrink-0 place-items-center">
                <Check
                  className={`h-3.5 w-3.5 ${checked ? 'text-brand-primary' : 'text-gray-300'}`}
                  strokeWidth={3}
                />
              </span>
              <span className="min-w-0 leading-4">
                <span className="block truncate text-sm font-semibold text-gray-900">
                  {branch.supplierName}
                </span>
                <span className="block truncate text-xs text-gray-500">
                  {/* The counter the engineer is standing at has no basket
                      line, so no fulfilment method to list — it says where
                      it came from instead of trailing an empty separator. */}
                  {[
                    branch.branchName,
                    branch.here
                      ? HERE_LABELS[branch.here]
                      : branch.methods
                          .map((method) => METHOD_LABELS[method])
                          .join(' + '),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </label>
          )
        })}
      </div>

      {/* Footer — Clear empties the draft; Apply commits it (an empty
          draft clears every pill). Padded for the phone's home indicator. */}
      <div className="sticky bottom-0 flex items-center gap-3 px-4 py-2.5 border-t border-gray-100 bg-white pb-[max(0.625rem,env(safe-area-inset-bottom))]">
        <Button variant="ghost" size="sm" onClick={() => setDraft([])} className="min-h-11">
          Clear
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="flex-1 min-h-11"
          onClick={() => {
            onApply(draft)
            onClose()
          }}
        >
          Apply{draft.length > 0 ? ` (${draft.length})` : ''}
        </Button>
      </div>
    </>
  )
}
